var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useState } from 'react';
import Input from '@rad/components/inputs/input';
import Dropdown from '@rad/components/inputs/dropdown';
import Modal from '@rad/components/ui/modal';
import { konstPromise } from '@rad/utility/promise';
import { FilePreviewCard } from '@rad/components/inputs/file-input-with-previews';
import { useCropModal } from './useCropModal';
import { CATEGORY_OPTIONS } from '../types';
import { update as updateAttachment, softDelete } from '../common/network/attachment';
import { useHeaderPositioner } from './useHeaderPositioner';
import { useToast } from '../contexts/toast';
const categoryOptions = CATEGORY_OPTIONS;
const imageMetaForm = {
    initial: (img) => ({ caption: img.caption || '', year: img.year || '', category: img.category || '' }),
    render: (form, onChange) => (_jsxs("div", { className: "d-flex flex-column gap-3", children: [_jsx(Input, { label: "Caption", kind: "text", value: form.caption, onChange: (val) => onChange(Object.assign(Object.assign({}, form), { caption: val })) }), _jsx(Input, { label: "Year", kind: "text", value: form.year, onChange: (val) => onChange(Object.assign(Object.assign({}, form), { year: val })) }), _jsx(Dropdown, { label: "Category", options: konstPromise(categoryOptions), optionKey: (x) => x.value, optionLabel: (x) => x.label, value: form.category || null, onChange: (val) => onChange(Object.assign(Object.assign({}, form), { category: val || '' })), required: true })] })),
    apply: (img, form) => (Object.assign(Object.assign({}, img), form)),
};
export const useImageHandler = (config) => {
    var _a;
    const { images, value, propertyId, onChange, canEditItem, isAdmin } = config;
    const renderer = (_a = config.metaFormRenderer) !== null && _a !== void 0 ? _a : imageMetaForm;
    const toast = useToast();
    const { cropStep, modalElement: cropModal } = useCropModal({ aspectRatio: 16 / 9 });
    const [editingMetaIndex, setEditingMetaIndex] = React.useState(null);
    const [metaForm, setMetaForm] = useState(null);
    const headerPositioner = useHeaderPositioner({ value, onChange });
    const buildDropdownActions = (index, actions) => {
        const image = images[index];
        const itemEditable = image ? canEditItem(image.addedBy) : isAdmin;
        return [
            ...(itemEditable && image ? [
                {
                    label: 'Edit Details',
                    icon: 'pencil',
                    onClick: () => {
                        setMetaForm(renderer.initial(image));
                        setEditingMetaIndex(index);
                    },
                },
            ] : []),
            ...(itemEditable ? [
                {
                    label: 'Make Primary',
                    icon: 'star',
                    hidden: images[index].isMain,
                    onClick: () => {
                        onChange(Object.assign(Object.assign({}, value), { attachments: images.map((a, i) => (Object.assign(Object.assign({}, a), { isMain: i === index }))) }));
                    },
                },
            ] : []),
            ...(itemEditable ? [
                { label: 'Delete', icon: 'trash', onClick: actions.remove, className: 'text-danger' },
            ] : []),
        ];
    };
    const imageHandler = {
        processSteps: [cropStep],
        onRemove: (i) => __awaiter(void 0, void 0, void 0, function* () {
            const image = images[i];
            if (!isAdmin && (!image || !canEditItem(image.addedBy)))
                return;
            const [success] = yield softDelete(image);
            if (!success)
                return;
            onChange(Object.assign(Object.assign({}, value), { attachments: images.filter((_, j) => j !== i).map((a, j) => (Object.assign(Object.assign({}, a), { sortOrder: j + 1 }))) }));
        }),
        renderPreview: (file, actions, index) => {
            return (_jsx("div", { className: "position-relative", children: _jsx(FilePreviewCard, { file: file, actions: actions, dropdownActions: buildDropdownActions(index, actions), maxPreviewSize: 240, isPrimary: images[index].isMain, imageUrl: images[index].url }) }));
        },
    };
    const metaModal = (_jsx(Modal, { isOpen: editingMetaIndex !== null, title: "Edit Image Details", onClose: () => setEditingMetaIndex(null), onSave: () => __awaiter(void 0, void 0, void 0, function* () {
            if (editingMetaIndex === null || metaForm === null)
                return;
            const att = images[editingMetaIndex];
            const updated = renderer.apply(att, metaForm);
            if (propertyId !== null) {
                const [success] = yield updateAttachment(updated, propertyId);
                if (!success)
                    return;
            }
            onChange(Object.assign(Object.assign({}, value), { attachments: images.map((a) => a.id === att.id ? updated : a) }));
            setEditingMetaIndex(null);
            toast.success('Image details saved');
        }), children: metaForm && renderer.render(metaForm, setMetaForm) }));
    const modalElement = _jsxs(_Fragment, { children: [cropModal, metaModal, headerPositioner.element] });
    return { imageHandler, modalElement };
};
